import { notification } from 'antd';
import { ArgsProps, NotificationPlacement } from 'antd/es/notification/interface';
import i18n from '../i18n/i18n';

type NotificationType = 'success' | 'info' | 'warning' | 'error';

// Default notification duration in seconds
const DEFAULT_DURATION = 4.5;

// Default placement for notifications
const DEFAULT_PLACEMENT = 'topRight';

/**
 * Main notification function to show Ant Design notifications
 * @param type Notification type
 * @param message The notification title/message
 * @param description Optional detailed description
 * @param options Additional notification configuration options
 */
export const showNotification = (
    type: NotificationType,
    message: string,
    description?: string,
    options?: Partial<ArgsProps>
) => {
    notification[type]({
        message,
        description,
        placement: DEFAULT_PLACEMENT,
        duration: DEFAULT_DURATION,
        ...options,
    });
};

/**
 * Show success notification
 */
export const showSuccess = (
    message: string,
    description?: string,
    placement: NotificationPlacement = 'topRight'
) => {
    notification.success({
        message,
        description,
        placement,
        style: {
            borderLeft: '4px solid #52c41a',
        },
    });
};

/**
 * Show error notification
 */
export const showError = (
    message: string,
    description?: string | null,
    placement: NotificationPlacement = 'topRight'
) => {
    // Kiểm tra nếu description là một error message đơn giản, cố gắng dịch nó
    let translatedDescription = description;
    if (description && typeof description === 'string' && description.startsWith('errors.')) {
        translatedDescription = i18n.t(description as any);
    }

    notification.error({
        message,
        description: translatedDescription || i18n.t('errors.somethingWentWrong' as any),
        placement,
        style: {
            borderLeft: '4px solid #ff4d4f',
        },
    });
};

/**
 * Show info notification
 */
export const showInfo = (
    message: string,
    description?: string,
    placement: NotificationPlacement = 'topRight'
) => {
    notification.info({
        message,
        description,
        placement,
        style: {
            borderLeft: '4px solid #1890ff',
        },
    });
};

/**
 * Show warning notification
 */
export const showWarning = (
    message: string,
    description?: string,
    placement: NotificationPlacement = 'topRight'
) => {
    notification.warning({
        message,
        description,
        placement,
        style: {
            borderLeft: '4px solid #faad14',
        },
    });
};

/**
 * Utility function for API calls to show appropriate notifications
 * @param apiCall The API function to execute
 * @param options Configuration options
 * @returns Promise with the API response
 */
export const withNotification = async <T>(
    apiCall: () => Promise<T>,
    options: {
        successMessage?: string;
        errorMessage?: string;
        showSuccessNotification?: boolean;
        showErrorNotification?: boolean;
        errorTransform?: (error: any) => { message: string; description?: string };
    } = {}
): Promise<T> => {
    const {
        successMessage = 'Thao tác thành công',
        errorMessage = 'Thao tác thất bại',
        showSuccessNotification = true,
        showErrorNotification = true,
        errorTransform,
    } = options;

    try {
        const result = await apiCall();

        if (showSuccessNotification) {
            showSuccess(successMessage);
        }

        return result;
    } catch (error: any) {
        if (showErrorNotification) {
            if (errorTransform) {
                const { message, description } = errorTransform(error);
                showError(message, description);
            } else {
                const description = error?.response?.data?.message || error?.message;
                showError(errorMessage, description);
            }
        }

        throw error;
    }
}; 